import React, { memo } from "react";
import { formats, validators } from "investira.sdk";
import { HighchartsReact } from "investira.react.charts";

const EmissoresChart = memo((props) => {
  const { data = [] } = props;

  const processData = (pData) => {
    const xData = Object.values(pData);
    const xTotal = xData.reduce((sum, item) => sum + item.financeiro, 0);

    const xThreshold = xTotal * 0.01; // 1% do total
    const xMainData = {};

    let xOthersSumFinanceiro = 0;
    let xOthersSumAtivos = 0;

    Object.entries(pData).forEach(([xKey, xItem]) => {
      if (xItem.financeiro >= xThreshold) {
        xMainData[xKey] = xItem;
      } else {
        xOthersSumFinanceiro += xItem.financeiro;
        xOthersSumAtivos += xItem.quantidade_ativos;
      }
    });

    if (xOthersSumFinanceiro > 0) {
      xMainData.OUTROS = {
        financeiro: xOthersSumFinanceiro,
        quantidade_ativos: xOthersSumAtivos,
      };
    }

    return xMainData;
  };

  const reduceData = (
    pData = [],
    pGroupBy = "emissor_top_setor_id",
    pNameKey = "top_setor",
    pIdKey = null,
    pDrilldown = null
  ) => {
    let xGrouped = pData.reduce((xAcc, xItem) => {
      const xKey = xItem[pGroupBy];

      if (!xAcc[xKey]) {
        xAcc[xKey] = {
          financeiro: 0,
          quantidade_ativos: 0,
          top_setor: "",
          setor: "",
          emissor_top_setor_id: "",
        };
      }

      xAcc[xKey].financeiro += xItem.financeiro;
      xAcc[xKey].quantidade_ativos += xItem.quantidade_ativos;
      xAcc[xKey].top_setor = xItem.top_setor;
      xAcc[xKey].setor = xItem.setor;
      xAcc[xKey].emissor_top_setor_id = xItem.emissor_top_setor_id;

      return xAcc;
    }, {});

    xGrouped = processData(xGrouped);

    const xResult = Object.entries(xGrouped).map(([xKey, xValue]) => {
      const xKeys = Object.keys(xGrouped).filter((key) => key !== "OUTROS");

      if (xKey === "OUTROS") {
        return {
          id: crypto.randomUUID(),
          name: "Outros",
          dataLabels: {
            color: "#FFFFFF", // Set the color of data labels to red
          },
          y: xValue.financeiro,
          drilldown: xKey,
          metadata: {
            quantidade_ativos: xValue.quantidade_ativos,
            financeiro: formats.formatNumber(xValue.financeiro, 2, true, true),
            ativos: pData
              .filter((xItem) => !xKeys.includes(xItem[pGroupBy]))
              .map((xItem) => xItem),
          },
        };
      }

      return {
        id: validators.isNull(pDrilldown) ? crypto.randomUUID() : pDrilldown,
        name: xValue[pNameKey],
        y: xValue.financeiro,
        dataLabels: {
          color: "#FFFFFF", // Set the color of data labels to red
        },
        ...(validators.isNull(pDrilldown) && { drilldown: xKey }),
        metadata: {
          quantidade_ativos: xValue.quantidade_ativos,
          financeiro: formats.formatNumber(xValue.financeiro, 2, true, true),
          ativos: pData
            .filter((xItem) => xItem[pGroupBy] === xKey)
            .map((xItem) => xItem),
        },
      };
    });

    return xResult;
  };

  const setorData = (pData) => {
    return pData.map((xItem) => {
      return {
        id: xItem.drilldown,
        name: xItem.name,
        data: reduceData(
          xItem.metadata.ativos,
          "emissor_setor_id",
          "setor",
          "emissor_top_setor_id",
          xItem.metadata.drilldown
        ),
      };
    });
  };

  const xTopSetor = reduceData(data, "emissor_top_setor_id", "top_setor");
  const xSetor = setorData(xTopSetor);

  const xOptions = {
    chart: {
      type: "pie",
      //width: "100%",
      height: "400px",
    },
    title: false,
    plotOptions: {
      pie: {
        innerSize: "50%",
        shadow: false,
        borderColor: "#2e2e46", // Cor do contorno (borda)
        borderWidth: 2, // Largura do contorno
        center: ["50%", "50%"],
        dataLabels: {
          enabled: true,
          useHTML: true,
          formatter: function () {
            return `<span style="color: #FFFFFF; text-decoration: none;">${this.point.name}</span>`;
          },
          style: {
            color: "#FFFFFF",
            textOutline: "none",
          },
        },
      },
    },
    tooltip: {
      useHTML: true,
      style: {
        zIndex: 1000, // Set a high z-index for the tooltip
      },
      pointFormat: `Financeiro: <b>{point.metadata.financeiro}</b><br>Quantidade de Ativos: <b>{point.metadata.quantidade_ativos}</b>`,
    },
    series: [
      {
        name: "Setor",
        colorByPoint: true,
        data: xTopSetor,
      },
    ],
    drilldown: {
      series: xSetor,
      breadcrumbs: {
        style: {
          color: "#FFFFFF",
          fill: "#FFFFFF", // Text color
        },
        buttonTheme: {
          style: {
            color: "#00dfa8", // Text color
          },
          states: {
            hover: {
              fill: "none", // Cor de fundo ao passar o mouse
            },
            select: {
              style: {
                color: "#FFFFFF",
                cursor: "default", // Cor do texto quando selecionado
              },
              fill: "none",
            },
          },
        },
      },
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 600,
          },
          chartOptions: {
            plotOptions: {
              pie: {
                dataLabels: {
                  distance: -20,
                  style: {
                    fontSize: "10px",
                  },
                },
              },
            },
          },
        },
      ],
    },
  };

  return (
    <HighchartsReact
      options={xOptions}
      containerProps={{
        style: {
          height: "100%",
          width: "100%",
        },
      }}
    />
  );
});

export default EmissoresChart;
